import React from 'react'
import { getImageURL } from '../../../lib/utils/getImageURL'
import GhostButton from '../../../components/Buttons/GhostButton'
import TextInput from '../../../components/FormElements/TextInput'
import SelectInput from '../../../components/FormElements/SelectInput'
import { hoursOptions, timezonesOptions } from '../../../lib/utils/timezones'
import { Vendor } from '../../../lib/types/Vendor'
import { useFormContext } from 'react-hook-form'
import { ReactComponent as DeleteIcon } from '../../../images/icons/action_delete.svg'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../../store/selectors/userSelector'
import states from '../../../lib/utils/states'
import countries from '../../../lib/utils/countries'

export default function SourceDealerVault({
  vendor,
  onRemove,
  index,
  sourceType,
}: {
  vendor: Vendor
  onRemove: (index: number) => void
  index: number
  sourceType: string
}): JSX.Element {
  const { watch } = useFormContext()
  const user = useSelector(currentUserSelector)
  const country = watch(`vendors.${index}.country`)

  const formValues = watch(`vendors[${index}]`)
  return (
    <div key={vendor.id} className={'flex flex-col gap-4 flex-1'}>
      {user?.role_id === 1 ? (
        <div>
          <div className={'flex items-center gap-3'}>
            <div className={'flex items-center justify-center'}>
              <div className={'w-7 h-7 rounded-full flex'}>
                <img
                  src={getImageURL(`images/vendors/${vendor.logo}`)}
                  className={'object-contain'}
                />
              </div>
            </div>
            <span className={'font-bold text-xs text-blue-primary flex-1'}>
              {vendor.name}
            </span>
            <GhostButton
              transparent={true}
              onClick={(e) => {
                e.preventDefault()
                onRemove(index)
              }}>
              <DeleteIcon />
            </GhostButton>
          </div>
          <div>
            <div className={'w-1/2'}>
              <TextInput
                rules={{ required: 'This field is required' }}
                inverted
                placeholder={`Ex. ${vendor.name} import`}
                name={`vendors.${index}.source_name`}
                label={'Name Your Source'}
                info={'Give it a clear name so you remember it easily'}
              />
            </div>
          </div>
          <div>
            <div className={'w-1/2'}>
              <TextInput
                inverted={true}
                rules={{
                  required: true,
                  validate: (value) => {
                    // regex value starts with DVD followed by numbers
                    const regex = /^DVD\d+$/
                    return regex.test(value) ? true : 'Invalid Dealer ID'
                  },
                }}
                label={'Dealer Vault Dealer ID'}
                name={`vendors.${index}.dv_dealer_id`}
                placeholder={'Eg: DVD12345'}
                info={`*Manual approval needed. It may take up to 24 hours to sync your ${sourceType} data.`}
              />
            </div>
          </div>
          <div className={'pb-10 border-b-2 flex justify-start items-center '}>
            <div className={'flex'}>
              <div className={'flex flex-row gap-5'}>
                <div className={'grow-0'}>
                  <SelectInput
                    required={'This field is required'}
                    name={`vendors.${index}.fetch_frequency`}
                    label={'Fetch frequency'}
                    options={[{ label: 'Daily', value: 'daily' }]}
                  />
                </div>
                <div className={'grow-0'}>
                  {formValues[`fetch_frequency`]?.value === 'weekly' && (
                    <SelectInput
                      required={'This field is required'}
                      name={`vendors.${index}.fetch_day`}
                      label={'Fetch day'}
                      options={[
                        { label: 'Monday', value: 'monday' },
                        { label: 'Tuesday', value: 'tuesday' },
                        { label: 'Wednesday', value: 'wednesday' },
                        { label: 'Thursday', value: 'thursday' },
                        { label: 'Friday', value: 'friday' },
                        { label: 'Saturday', value: 'saturday' },
                        { label: 'Sunday', value: 'sunday' },
                      ]}
                    />
                  )}
                </div>
                <div className={'grow-0'}>
                  {formValues[`fetch_frequency`] &&
                    formValues[`fetch_frequency`]?.value !== 'one_time' && (
                      <SelectInput
                        required={'This field is required'}
                        disabled={
                          formValues[`fetch_frequency`]?.value === 'one_time' ||
                          !formValues[`fetch_frequency`]
                        }
                        name={`vendors.${index}.fetch_time`}
                        label={'Fetch time'}
                        options={hoursOptions}
                      />
                    )}
                </div>
                <div className={'grow-0'}>
                  {formValues[`fetch_frequency`] &&
                    formValues[`fetch_frequency`]?.value !== 'one_time' && (
                      <SelectInput
                        required={'This field is required'}
                        disabled={
                          formValues[`fetch_frequency`]?.value === 'one_time' ||
                          !formValues[`fetch_frequency`]
                        }
                        name={`vendors.${index}.fetch_timezone`}
                        label={'Timezone'}
                        options={timezonesOptions}
                      />
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 px-5">
          <span className="font-bold">Dealership Location Information</span>
          <TextInput
            rules={{ required: 'This field is required' }}
            inverted
            placeholder={`Dealership Name`}
            name={`vendors.${index}.dealership_name`}
            label={'Dealership Name'}
          />
          <TextInput
            rules={{ required: 'This field is required' }}
            inverted
            placeholder={`Dealership Address`}
            name={`vendors.${index}.dealership_address`}
            label={'Dealership Address'}
          />
          <div className="grid grid-cols-2 gap-3 w-full">
            <TextInput
              rules={{ required: 'This field is required' }}
              inverted
              placeholder={`Dealership City`}
              name={`vendors.${index}.city`}
              label={'City'}
            />
            <SelectInput
              required
              placeholder={`Dealership State`}
              name={`vendors.${index}.state`}
              label={'State'}
              options={states.filter((state) => state.country === country?.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 w-full">
            <SelectInput
              required
              placeholder={`Country`}
              name={`vendors.${index}.country`}
              label={'Country'}
              options={countries}
            />
            <TextInput
              rules={{ required: 'This field is required' }}
              inverted
              placeholder={`Zip Code`}
              name={`vendors.${index}.zipcode`}
              label={'Zip Code'}
            />
          </div>
          <span className="font-bold">Dealership Contact Information</span>
          <TextInput
            rules={{ required: 'This field is required' }}
            inverted
            placeholder={`Dealer Contact Full Name`}
            name={`vendors.${index}.full_name`}
            label={'Full Name'}
          />
          <TextInput
            rules={{ required: 'This field is required' }}
            inverted
            placeholder={`Dealer Contact Email`}
            name={`vendors.${index}.email`}
            label={'E-mail Address'}
          />
          <TextInput
            rules={{ required: 'This field is required' }}
            inverted
            placeholder={`Dealer Contact Phone`}
            name={`vendors.${index}.phone`}
            label={'Phone Number'}
          />
        </div>
      )}
    </div>
  )
}
